import type { Tables } from '../../types/database.types';

export type TaskComment = Tables<'task_comments'>;
export type CommentMention = Tables<'comment_mentions'>;

/** A comment joined to its author's readable identity and its mentions. */
export interface CommentWithAuthor {
  id: string;
  /** Null for a project-level discussion comment. */
  task_id: string | null;
  project_id: string;
  author_id: string;
  body: string;
  /** Set by the database when the body changes after creation; never sent. */
  edited_at: string | null;
  created_at: string;
  /**
   * Set on a Messages thread reply (Drop 3c). Read with the message itself so
   * the room can keep a reply out of its main list even before, or without,
   * the separate extras read (Dev4 N2). Optional so fixtures need not set it.
   */
  parent_comment_id?: string | null;
  quoted_comment_id?: string | null;
  /** Empty when the author's profile is unreadable; render a neutral fallback. */
  author_name: string;
  /** The author's picture, same column the Members card and the Inbox use; null means initials. */
  author_avatar_url: string | null;
  /** The ids of the people @named in this comment. */
  mentioned_user_ids: string[];
}

export interface CreateCommentInput {
  quotedCommentId?: string | null;
  /** Null posts to the project-level discussion thread. */
  taskId: string | null;
  projectId: string;
  /** Set instead of a project when the thread belongs to a deal. */
  dealId?: string | null;
  /** Set instead of a project when the thread belongs to a social post. */
  socialPostId?: string | null;
  body: string;
  /** The people @named in the body. Mentions never grant access (handoff 8). */
  mentionedUserIds: string[];
}

/** The database refuses bodies beyond this; the composer stops earlier. */
export const COMMENT_BODY_MAX_LENGTH = 10000;

/**
 * How many comments each task has, for the List View row indicator.
 * One project-wide read, counted client-side; no per-row request.
 */
export type CommentCountsByTask = Record<string, number>;

export function countCommentsByTask(rows: { task_id: string | null }[]): CommentCountsByTask {
  const counts: CommentCountsByTask = {};
  for (const row of rows) {
    // Project-level discussion comments belong to no task row.
    if (row.task_id !== null) {
      counts[row.task_id] = (counts[row.task_id] ?? 0) + 1;
    }
  }
  return counts;
}

/**
 * The @handles in a comment body, in order of first appearance. A handle is
 * matched against project members by the composer; this only finds the
 * candidates. Written for Stage 4's autocomplete but owned here so the
 * matching rule has exactly one home.
 */
export function extractMentionHandles(body: string): string[] {
  const seen = new Set<string>();
  const handles: string[] = [];
  for (const match of body.matchAll(/(^|\s)@([\p{L}\p{N}._-]+)/gu)) {
    const handle = (match[2] ?? '').toLowerCase();
    if (handle !== '' && !seen.has(handle)) {
      seen.add(handle);
      handles.push(handle);
    }
  }
  return handles;
}
